import type { ModelRoute, VelugaModelCatalogItem } from '../types.js';
import { parsePositiveInteger } from './model-limit-input.js';

export interface TenantCustomModelRouteForm {
  customId: string;
  customName: string;
  backendId: string;
  endpointRef: string;
  credentialRef: string;
  contextWindow: string;
  maxOutputTokens: string;
}

export type TenantCustomModelRouteInput =
  | {
      ok: true;
      route: ModelRoute;
      catalogItem: VelugaModelCatalogItem;
    }
  | { ok: false; errorKey: 'models.customModelLimitsInvalid' };

export function buildTenantCustomModelRouteInput(
  tenantId: string,
  form: TenantCustomModelRouteForm
): TenantCustomModelRouteInput {
  const modelId = form.customId.trim();
  const contextWindow = parsePositiveInteger(form.contextWindow);
  const maxOutputTokens = parsePositiveInteger(form.maxOutputTokens);
  if (!contextWindow || !maxOutputTokens) {
    return { ok: false, errorKey: 'models.customModelLimitsInvalid' };
  }

  const route: ModelRoute = {
    id: `rt_${modelId.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`,
    tenantId,
    catalogModelId: modelId,
    routeKind: 'tenant-custom',
    providerProtocol: 'openai-compatible',
    endpointRef: form.endpointRef,
    credentialRef: form.credentialRef,
    managedBy: 'tenant-admin',
    status: 'active'
  };
  const catalogItem: VelugaModelCatalogItem = {
    id: modelId,
    provider: 'custom',
    displayName: form.customName.trim() || modelId,
    routeKind: 'tenant-custom',
    capabilities: {
      reasoning: true,
      vision: false,
      toolUse: true,
      streaming: true,
      structuredOutput: true,
      citationStrict: true,
      longContext: false
    },
    limits: {
      contextWindow,
      maxOutputTokens
    },
    policyTags: ['tenant-custom'],
    routing: {
      mode: 'fixed',
      candidates: [
        {
          tier: 'strong',
          routeBackend: {
            backendId: form.backendId.trim(),
            protocol: 'openai-compatible'
          }
        }
      ],
      safety: {
        allowAdaptiveForRisk: [],
        disallowForDataClassifications: ['confidential', 'secret'],
        requireStrongForFinalAnswer: true,
        requireStrongForCompliance: true
      }
    },
    status: 'active'
  };
  return { ok: true, route, catalogItem };
}
